import type { Ref } from '@nuxtjs/composition-api';
import { onScopeDispose, ref, watch } from '@nuxtjs/composition-api';
import type { UseIntersectionObserverReturn } from '@vueuse/core';
import { useDebounceFn, useIntersectionObserver, useWindowScroll, watchThrottled } from '@vueuse/core';

import { useScreenSize } from '~/diptyqueTheme/composable';

export function useFixedPanel(footerElement: Ref<HTMLElement | null>, actionPanelElement: Ref<HTMLElement | null>, actionPanelElementOffset = 0) {
  const { isDesktop } = useScreenSize();
  const isPanelFixed = ref(false);

  // Debounce time for intersection observer callbacks
  const INTERSECTION_DEBOUNCE = 100;

  // Action panel intersection flag
  // - show/hide fixed panel when intersecting with action panel
  const actionsIntersecting = ref(false);

  // Flag: have we scrolled past the action panel (element moved above viewport)?
  const actionsPassed = ref(false);

  // Footer intersection flag
  // - show/hide fixed panel when intersecting with footer
  const footerIntersecting = ref(false);

  let actionIntersectionObserver: UseIntersectionObserverReturn = undefined;
  let footerIntersectionObserver: UseIntersectionObserverReturn = undefined;

  // Create debounced callbacks once to avoid reallocating functions on each watch run
  const onActionIntersect = useDebounceFn(([entry]: IntersectionObserverEntry[]) => {
    if (!entry) {
      actionsIntersecting.value = false;
      actionsPassed.value = false;
      return;
    }

    // Only set true when desktop, observers will be stopped when not desktop, but guard anyway
    // Also ensure we have scrolled past the element to avoid flicker when element is partially visible
    actionsIntersecting.value = !!entry.isIntersecting && isDesktop.value;

    // Fully "scrolled past" when the entire element is above the viewport.
    const bottom = entry.boundingClientRect.bottom;
    actionsPassed.value = !entry.isIntersecting && bottom < actionPanelElementOffset && isDesktop.value;
  }, INTERSECTION_DEBOUNCE);

  const onFooterIntersect = useDebounceFn(([entry]: IntersectionObserverEntry[]) => {
    footerIntersecting.value = !!entry?.isIntersecting;
  }, INTERSECTION_DEBOUNCE);

  const cleanupActionObserver = () => {
    if (actionIntersectionObserver) {
      try {
        actionIntersectionObserver?.stop();
        actionIntersectionObserver = undefined;
      } catch {
        // do nothing
      }
    }
  };

  const cleanupFooterObserver = () => {
    if (footerIntersectionObserver) {
      try {
        footerIntersectionObserver?.stop();
        footerIntersectionObserver = undefined;
      } catch {
        // do nothing
      }
    }
  };

  // Watch actionPanelElement and isDesktop together to create/stop observer when either changes.
  // This prevents running observers on mobile and avoids recreating them unnecessarily.
  watch(
    [actionPanelElement, isDesktop],
    ([el, desktop]) => {
      // Clean up previous observer if present
      cleanupActionObserver();

      // Reset flags when observer is removed
      actionsIntersecting.value = false;
      // On mobile, consider actions as "passed"
      actionsPassed.value = !desktop;

      if (el && desktop) {
        actionIntersectionObserver = useIntersectionObserver(actionPanelElement, onActionIntersect);
      }
    },
    { immediate: true }
  );

  // Watch for footer element and set up observer when available
  watch(
    footerElement,
    (el) => {
      // Clean up previous observer if present
      cleanupFooterObserver();

      // Reset flag when observer is removed
      footerIntersecting.value = false;

      if (el) {
        footerIntersectionObserver = useIntersectionObserver(footerElement, onFooterIntersect);
      }
    },
    { immediate: true }
  );

  // Track scroll position from the top
  const { y } = useWindowScroll();
  const hasScrolled = ref(false);
  const SCROLL_THROTTLE_MIN = 50;
  watchThrottled(
    y,
    (newY) => {
      hasScrolled.value = newY > 0;
    },
    { throttle: Math.max(SCROLL_THROTTLE_MIN, INTERSECTION_DEBOUNCE) }
  );

  // Watch for scroll, footer and action panel intersection
  watch(
    [hasScrolled, footerIntersecting, actionsIntersecting, actionsPassed],
    ([scrolled, footerIntersecting, actionsIntersecting, actionsPassed]) => {
      isPanelFixed.value = scrolled && !footerIntersecting && !actionsIntersecting && actionsPassed;
    },
    { immediate: true }
  );

  // Cleanup intersection observers
  onScopeDispose(() => {
    cleanupActionObserver();
    cleanupFooterObserver();
  });

  return {
    isPanelFixed,
    hasScrolled,
    actionsIntersecting,
    actionsPassed,
    footerElement,
    actionPanelElement
  };
}

export default useFixedPanel;
